import { isType, Action } from "typescript-fsa";
import update from "immutability-helper";
import log from "loglevel";
import { Map } from "immutable";

import { isFailedResponseUnauthenticatedNotLogin } from "@api/actions";
import { logout } from "@auth/actions";
import {
    PaginationResult,
    makeMapLoadedModel,
    makeMergeEntities,
    makeHandleShowFailure,
    HttpStateError,
} from "@orm/models";
import * as actions from "../actions";
import {
    Filters,
    Delivery,
    Batch,
    Type,
    StatusType,
    Status,
    DeliveryNormalisedEntities,
    DeliveryInternalState as State,
} from "../models";

const getInitialState = () => ({
    entities: {
        deliveries: Map<string, Delivery>(),
        deliveryBatches: Map<string, Batch>(),
        deliveryTypes: Map<string, Type>(),
        deliveryStatuses: Map<string, Status>(),
        deliveryStatusTypes: Map<string, StatusType>(),
    },
    pagination: null,
    list: [],
    filters: {
        types: [],
        statuses: [],
        dspIds: [],
        albumIds: [],
    },
    requests: {
        indexLastLoaded: null,
        indexError: null,
    },
} as State);

const mapLoadedDelivery = makeMapLoadedModel<Delivery, string>("delivery", Delivery);
const mergeEntities = makeMergeEntities<State, DeliveryNormalisedEntities>(
    ["deliveries", "deliveryBatches", "deliveryTypes", "deliveryStatuses", "deliveryStatusTypes"],
    { deliveries: mapLoadedDelivery },
);
const deliveriesShowFailure = makeHandleShowFailure<State, Delivery, DeliveryNormalisedEntities>(
    Delivery,
    "deliveries",
    false,
);

function reduce(state: State = getInitialState(), action: actions.DeliveriesAction): State {
    // State should never be null...
    if (state === null) {
        log.warn("deliveries state setting to null, resetting to initialState.");
        state = {} as State;
        state = update(state, { $set: getInitialState() });
    }

    if (isType(action, actions.setFilters)) {
        return setFilters(state, action.payload);
    }
    else if (isType(action, actions.resetFilters)) {
        return setFilters(state);
    }
    else if (isType(action, actions.deliveriesIndexSuccess)) {
        // Successful deliveriesIndex
        return deliveriesIndexSuccess(state, action);
    }
    else if (isType(action, actions.deliveriesIndexFailure)) {
        // Failed deliveriesIndex
        return deliveriesIndexFailure(state, action);
    }
    else if (isType(action, actions.deliveriesShowSuccess)) {
        // Successful deliveriesShow
        return mergeEntities(state, action.payload.entities, "delivery");
    }
    else if (isType(action, actions.deliveriesShowFailure)) {
        // Failed deliveriesShow
        return deliveriesShowFailure(state, action.payload);
    }
    else if (isType(action, logout) || isFailedResponseUnauthenticatedNotLogin(action)) {
        // Clear the state on logout.
        return update(state, { $set: getInitialState() });
    }
    // Otherwise we do nothing
    return state;
}

function setFilters(state: State, filters: Filters = {
    albumIds: [],
    dspIds: [],
    statuses: [],
    types: [],
}) {
    return update(state, {
        filters: { $set: filters },
    });
}

function deliveriesIndexSuccess(state: State, action: Action<[PaginationResult, actions.DeliveriesResult]>) {
    const pagination = action.payload[0];
    const { result, entities } = action.payload[1];
    // If the pagination is the first page, replace all the deliveries.
    let fullList = state.list.concat(result);
    if (pagination.page === 1) {
        fullList = result;
    }
    const newState = update(state, {
        pagination: { $set: pagination },
        list: { $set: fullList },
        requests: {
            $merge: {
                indexLastLoaded: new Date(),
                indexError: null,
            },
        },
    });

    return mergeEntities(newState, entities, "delivery");
}

function deliveriesIndexFailure(state: State, action: Action<HttpStateError>): State {
    return update(state, {
        requests: { $merge: { indexError: action.payload } },
    });
}

export { reduce as deliveries, getInitialState };
